import { ApiClientError, api, auth } from "@lumberset/api-client";
import {
  HelpLauncher,
  ReportProblemButton,
  ReportProblemDialog,
  useReportProblem,
} from "@lumberset/support-ui";
import { initials } from "@lumberset/takeoff-ui";
import {
  AppShell,
  Avatar,
  Banner,
  Button,
  CommandPalette,
  DropdownMenu,
  EmptyState,
  Kbd,
  navItemStyles,
  Sidebar,
} from "@lumberset/ui";
import {
  ArrowPath,
  ArrowRightOnRectangle,
  BookOpen,
  Bug,
  Buildings,
  CogSixTooth,
  CreditCard,
  DocumentText,
  EllipsisHorizontal,
  Envelope,
  Eye,
  House,
  Lifebuoy,
  MagnifyingGlass,
  Plus,
  QuestionMarkCircle,
  Tag,
  Users,
} from "@medusajs/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useState } from "react";
import { projectsQuery, yardQuery } from "../lib/queries";

/** Everything behind sign-in. Ensures a session and a Yard before any screen loads. */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    const { data: session } = await auth.getSession();
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });

    try {
      const yard = await context.queryClient.ensureQueryData(yardQuery);
      return { user: session.user, yard };
    } catch (error) {
      if (!(error instanceof ApiClientError) || error.code !== "no_active_yard") throw error;
      // In several workspaces with none chosen yet, as after signing in: open the first
      // one, and the workspace menu switches to the others. In none: set one up.
      const { data: workspaces } = await auth.organization.list();
      const first = workspaces?.[0];
      if (!first) throw redirect({ to: "/onboarding" });
      await auth.organization.setActive({ organizationId: first.id });
      const yard = await context.queryClient.fetchQuery(yardQuery);
      return { user: session.user, yard };
    }
  },
  component: AppLayout,
  errorComponent: ({ error, reset }) => (
    <div className="flex min-h-dvh items-center justify-center">
      <EmptyState
        title="Something went wrong"
        description={error instanceof Error ? error.message : "Please try again."}
        action={
          <Button variant="secondary" onClick={reset}>
            Try again
          </Button>
        }
      />
    </div>
  ),
});

/** Signs out, forgets everything cached, and lands on the sign-in screen. */
function useSignOut() {
  const router = useRouter();
  const queryClient = useQueryClient();
  return async () => {
    await auth.signOut();
    queryClient.clear();
    await router.invalidate();
    router.history.push("/login");
  };
}

/**
 * Across the top while an Admin is signed in as this Member, so it is never
 * mistaken for the Member's own session. Stop signs out of the borrowed
 * session; the Admin's own panel is untouched, it lives on another host.
 */
function SignedInAsBanner({
  name,
  workspace,
  adminName,
}: {
  name: string;
  workspace: string;
  adminName: string;
}) {
  const signOut = useSignOut();
  const [stopping, setStopping] = useState(false);
  return (
    <Banner
      icon={<Eye />}
      action={
        <Button
          size="small"
          variant="secondary"
          isLoading={stopping}
          onClick={async () => {
            setStopping(true);
            await signOut();
          }}
        >
          Stop
        </Button>
      }
    >
      Signed in as {name} in {workspace} by Admin {adminName}.{" "}
      <span className="max-sm:hidden">Everything done here is logged under the Admin's name.</span>
    </Banner>
  );
}

function AppLayout() {
  const onProjects = useRouterState({ select: (state) => state.location.pathname }) === "/projects";
  const { user, yard } = Route.useRouteContext();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const report = useReportProblem();
  // A Builder buys lumber: it has no customers, catalog or quotes of its own, and sends RFQs instead.
  const builder = yard.kind === "builder";

  return (
    <>
      <AppShell
        banner={
          yard.impersonation ? (
            <SignedInAsBanner
              name={user.name}
              workspace={yard.name}
              adminName={yard.impersonation.adminName}
            />
          ) : null
        }
        sidebar={
          <>
            <Sidebar.Header>
              <YardMenu
                yardId={yard.id}
                yardName={yard.name}
                roleLabel={yard.roleLabel}
                builder={builder}
              />
            </Sidebar.Header>

            <Sidebar.Body>
              <button type="button" className={navItemStyles} onClick={() => setSearchOpen(true)}>
                <MagnifyingGlass />
                <span className="flex-1">Search</span>
                <Kbd className="hidden lg:inline-flex">Ctrl K</Kbd>
              </button>

              <div className="h-2" />

              <Sidebar.Item asChild>
                <Link to="/" activeOptions={{ exact: true }}>
                  <House /> Home
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/projects" search={{}}>
                  <Buildings /> Projects
                </Link>
              </Sidebar.Item>
              {builder ? (
                <Sidebar.Item asChild>
                  <Link to="/rfqs">
                    <Envelope /> RFQs
                  </Link>
                </Sidebar.Item>
              ) : (
                <>
                  <Sidebar.Item asChild>
                    <Link to="/quotes">
                      <DocumentText /> Quotes
                    </Link>
                  </Sidebar.Item>
                  <Sidebar.Item asChild>
                    <Link to="/customers">
                      <Users /> Customers
                    </Link>
                  </Sidebar.Item>
                  <Sidebar.Item asChild>
                    <Link to="/catalog">
                      <Tag /> Catalog
                    </Link>
                  </Sidebar.Item>
                </>
              )}
            </Sidebar.Body>

            <Sidebar.Footer>
              <Sidebar.Item asChild>
                <Link to="/settings">
                  <CogSixTooth /> Settings
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/help">
                  <QuestionMarkCircle /> Help
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/support">
                  <Lifebuoy /> Support
                </Link>
              </Sidebar.Item>
              <ReportProblemButton onClick={report.start} isLoading={report.capturing} />
              <UserMenu
                name={user.name}
                email={user.email}
                image={user.image}
                onReport={report.start}
              />
            </Sidebar.Footer>
          </>
        }
        breadcrumbs={<div id="breadcrumbs" />}
        actions={
          // The Projects page has its own New project button; one per screen is enough.
          onProjects ? null : (
            <Button asChild size="small" variant="secondary">
              <Link to="/projects" search={{ new: true }}>
                <Plus /> New project
              </Link>
            </Button>
          )
        }
      >
        <Outlet />
      </AppShell>

      <Search
        open={searchOpen}
        onOpenChange={setSearchOpen}
        builder={builder}
        onReport={report.start}
      />
      <ReportProblemDialog
        client={api}
        {...report.dialog}
        onViewTicket={(id) => navigate({ to: "/support/$id", params: { id } })}
        onViewTickets={() => navigate({ to: "/support" })}
      />
      <HelpLauncher api={api} onOpenTicket={report.start} />
    </>
  );
}

/**
 * The other workspaces this person is in, and switching to one: the session's
 * active workspace changes, and everything cached belonged to the old one.
 */
function useOtherWorkspaces(currentId: string) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await auth.organization.list()).data ?? [],
  });
  const others = (data ?? []).filter((workspace) => workspace.id !== currentId);
  const switchTo = async (organizationId: string) => {
    await auth.organization.setActive({ organizationId });
    queryClient.clear();
    await router.invalidate();
    router.history.push("/");
  };
  return { others, switchTo };
}

function YardMenu({
  yardId,
  yardName,
  roleLabel,
  builder,
}: {
  yardId: string;
  yardName: string;
  roleLabel: string;
  builder: boolean;
}) {
  const { others, switchTo } = useOtherWorkspaces(yardId);
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger className="transition-fg flex w-full items-center gap-x-2.5 rounded-md p-1 text-left outline-none hover:bg-ui-bg-subtle-hover focus-visible:shadow-borders-focus data-[state=open]:bg-ui-bg-subtle-hover">
        <Avatar fallback={initials(yardName)} size="small" />
        <span className="min-w-0 flex-1">
          <span className="txt-compact-small-plus block truncate text-ui-fg-base">{yardName}</span>
          <span className="txt-compact-xsmall block text-ui-fg-muted">{roleLabel}</span>
        </span>
        <EllipsisHorizontal className="shrink-0 text-ui-fg-muted" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="w-[236px]">
        <DropdownMenu.Item asChild>
          <Link to="/settings">
            <Buildings /> {builder ? "Company settings" : "Yard settings"}
          </Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <Link to="/settings/team">
            <Users /> Team
          </Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <Link to="/settings/billing">
            <CreditCard /> Billing
          </Link>
        </DropdownMenu.Item>
        {others.length > 0 ? (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label>Switch workspace</DropdownMenu.Label>
            {others.map((workspace) => (
              <DropdownMenu.Item key={workspace.id} onSelect={() => switchTo(workspace.id)}>
                <ArrowPath />
                <span className="truncate">{workspace.name}</span>
              </DropdownMenu.Item>
            ))}
          </>
        ) : null}
        <DropdownMenu.Separator />
        <DropdownMenu.Item asChild>
          <a href="https://lumberset.com/how-it-works" target="_blank" rel="noreferrer">
            <BookOpen /> How LumberSet works
          </a>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}

function UserMenu({
  name,
  email,
  image,
  onReport,
}: {
  name: string;
  email: string;
  image?: string | null;
  onReport: () => void;
}) {
  const signOut = useSignOut();

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger className="transition-fg flex w-full items-center gap-x-2.5 rounded-md p-1 text-left outline-none hover:bg-ui-bg-subtle-hover focus-visible:shadow-borders-focus data-[state=open]:bg-ui-bg-subtle-hover">
        <Avatar src={image} fallback={initials(name)} size="xsmall" shape="circle" />
        <span className="txt-compact-small-plus min-w-0 flex-1 truncate text-ui-fg-subtle">
          {name}
        </span>
        <EllipsisHorizontal className="shrink-0 text-ui-fg-muted" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content side="top" className="w-[236px]">
        <DropdownMenu.Label className="truncate">{email}</DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Item asChild>
          <Link to="/support">
            <Lifebuoy /> Support
          </Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={onReport}>
          <Bug /> Report a problem
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={signOut}>
          <ArrowRightOnRectangle /> Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}

function Search({
  open,
  onOpenChange,
  builder,
  onReport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  builder: boolean;
  onReport: () => void;
}) {
  const navigate = useNavigate();
  const { data } = useQuery({ ...projectsQuery(), enabled: open });

  const go = (action: () => Promise<void>) => {
    onOpenChange(false);
    void action();
  };

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      placeholder="Find a project, or jump to..."
    >
      <CommandPalette.Group heading="Go to">
        <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/projects", search: {} }))}>
          <Buildings /> Projects
        </CommandPalette.Item>
        {builder ? (
          <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/rfqs" }))}>
            <Envelope /> RFQs
          </CommandPalette.Item>
        ) : (
          <>
            <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/quotes" }))}>
              <DocumentText /> Quotes
            </CommandPalette.Item>
            <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/customers" }))}>
              <Users /> Customers
            </CommandPalette.Item>
            <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/catalog" }))}>
              <Tag /> Catalog
            </CommandPalette.Item>
          </>
        )}
        <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/settings" }))}>
          <CogSixTooth /> Settings
        </CommandPalette.Item>
        <CommandPalette.Item onSelect={() => go(() => navigate({ to: "/support" }))}>
          <Lifebuoy /> Support
        </CommandPalette.Item>
      </CommandPalette.Group>

      <CommandPalette.Group heading="Help">
        <CommandPalette.Item
          value="Report a problem bug question feedback"
          onSelect={() => {
            onOpenChange(false);
            onReport();
          }}
        >
          <Bug /> Report a problem
        </CommandPalette.Item>
      </CommandPalette.Group>

      {data && data.projects.length > 0 ? (
        <CommandPalette.Group heading="Projects">
          {data.projects.slice(0, 30).map((project) => (
            <CommandPalette.Item
              key={project.id}
              value={`${project.name} ${project.customerName ?? ""}`}
              onSelect={() =>
                go(() => navigate({ to: "/projects/$id", params: { id: project.id } }))
              }
            >
              <Buildings />
              <span className="flex-1 truncate">{project.name}</span>
              {project.customerName ? (
                <span className="txt-compact-small text-ui-fg-muted">{project.customerName}</span>
              ) : null}
            </CommandPalette.Item>
          ))}
        </CommandPalette.Group>
      ) : null}
    </CommandPalette>
  );
}
